/**
 * Button state management for RMC stepper
 * Handles disabled states and styling based on validation
 */

import {useMemo} from 'react'

/**
 * Button styling configuration based on validation state
 */
export const useStepperButtonStyles = (isCurrentStepValid: boolean, isValidating: boolean) => {
  const buttonStyles = useMemo(
    () => ({
      // Continue button styles
      continueButton: {
        flex: 1,
        backgroundColor: isCurrentStepValid ? '#016AD4' : '#E6E6E6',
        color: isCurrentStepValid ? '#FFFFFF' : '#A1A1A1',
        opacity: isValidating ? 0.7 : 1,
        cursor: isCurrentStepValid && !isValidating ? 'pointer' : 'not-allowed',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: isCurrentStepValid ? '#0056B3' : '#E6E6E6',
          boxShadow: isCurrentStepValid ? '0px 4px 12px 0px rgba(31,73,94,0.2)' : 'none'
        },
        '&:disabled': {
          backgroundColor: '#E6E6E6',
          color: '#A1A1A1',
          cursor: 'not-allowed',
          boxShadow: 'none'
        }
      },

      // Back button styles (always enabled when visible)
      backButton: {
        flex: 1,
        color: '#016AD4',
        fontWeight: 400,
        fontSize: '16px',
        height: '48px',
        borderRadius: '6px',
        textTransform: 'none' as const,
        boxShadow: '0px 2px 8px 0px rgba(31,73,94,0.15)',
        '&:hover': {
          backgroundColor: 'transparent',
          boxShadow: '0px 4px 12px 0px rgba(31,73,94,0.2)'
        }
      },

      // Discard button styles
      discardButton: {
        color: '#DA0901',
        fontWeight: 400,
        fontSize: '16px',
        height: '48px',
        px: 0,
        borderRadius: '6px',
        textTransform: 'none' as const,
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    }),
    [isCurrentStepValid, isValidating]
  )

  return buttonStyles
}

/**
 * Button text based on validation and loading states
 */
export const useStepperButtonText = (isCurrentStepValid: boolean, isValidating: boolean) => {
  return useMemo(() => {
    if (isValidating) {
      return 'Validating...'
    }
    if (!isCurrentStepValid) {
      return 'Select and continue'
    }
    return 'Select and continue'
  }, [isCurrentStepValid, isValidating])
}

/**
 * Hook for complete button state management
 */
export const useStepperButtonState = (isCurrentStepValid: boolean, isValidating: boolean) => {
  const styles = useStepperButtonStyles(isCurrentStepValid, isValidating)
  const buttonText = useStepperButtonText(isCurrentStepValid, isValidating)

  return {
    styles,
    buttonText,
    isDisabled: !isCurrentStepValid || isValidating,
    showValidating: isValidating
  }
}
